import React, { useState } from 'react';
import {
  ONBOARDING_INTENT_OPTIONS,
  buildStoryCards,
} from '../../constants/onboardingContent';
import { markOnboardingCompleted } from '../../utils/onboardingStorage';

export default function ProductOnboardingFlow({ onClose }) {
  const [phase, setPhase] = useState('intent');
  const [selectedIntents, setSelectedIntents] = useState([]);
  const [cardIndex, setCardIndex] = useState(0);
  const [cards, setCards] = useState([]);

  const toggleIntent = (id) => {
    setSelectedIntents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const finish = (intents) => {
    markOnboardingCompleted(intents);
    onClose?.();
  };

  const handleSkip = () => finish(selectedIntents);

  const startStories = () => {
    const storyCards = buildStoryCards(selectedIntents.length ? selectedIntents : ['exploring']);
    setCards(storyCards);
    setCardIndex(0);
    setPhase('stories');
  };

  const handleNextCard = () => {
    if (cardIndex >= cards.length - 1) {
      finish(selectedIntents.length ? selectedIntents : ['exploring']);
      return;
    }
    setCardIndex((i) => i + 1);
  };

  const currentCard = cards[cardIndex];

  return (
    <div
      className="fixed inset-0 z-[13000] flex items-center justify-center bg-black/55 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flea-onboarding-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-hidden flex flex-col">
        {phase === 'intent' ? (
          <>
            <div className="p-6 pb-4 overflow-y-auto">
              <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wide mb-2">Welcome</p>
              <h2 id="flea-onboarding-title" className="text-2xl font-bold text-gray-900 mb-2">
                What brings you to Flea?
              </h2>
              <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                Pick one or more — we&apos;ll tailor a quick tour to what you care about.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ONBOARDING_INTENT_OPTIONS.map((option) => {
                  const active = selectedIntents.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => toggleIntent(option.id)}
                      className={`text-left rounded-xl border-2 p-4 transition-all ${
                        active
                          ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-emerald-200'
                      }`}
                    >
                      <span className="text-2xl block mb-2" aria-hidden>{option.icon}</span>
                      <span className="font-semibold text-gray-900 text-sm leading-snug">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={handleSkip}
                className="flex-1 py-3 px-4 rounded-lg border border-gray-300 bg-white text-gray-800 font-semibold hover:bg-gray-100"
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={startStories}
                className="flex-1 py-3 px-4 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
              >
                Show me around
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-8 pb-4 flex-1 overflow-y-auto text-center">
              <div className="text-5xl mb-4" aria-hidden>{currentCard?.icon}</div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">{currentCard?.title}</h2>
              <p className="text-gray-600 text-sm leading-relaxed max-w-md mx-auto">{currentCard?.body}</p>
              <div className="flex justify-center gap-2 mt-8">
                {cards.map((card, idx) => (
                  <span
                    key={card.id}
                    className={`h-2 rounded-full transition-all ${
                      idx === cardIndex ? 'w-6 bg-emerald-600' : 'w-2 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {cardIndex + 1} of {cards.length}
              </p>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={handleSkip}
                className="py-3 px-4 rounded-lg border border-gray-300 bg-white text-gray-800 font-semibold hover:bg-gray-100"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleNextCard}
                className="flex-1 py-3 px-4 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
              >
                {cardIndex >= cards.length - 1 ? 'Start exploring' : 'Next'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
